/**
 * Frontend view types + PURE display helpers for the SLA / work-leakage queue
 * (TRAIN-D-V4-P0-SLA-LEAKAGE-ENGINE-V1). Mirrors the backend
 * lib/sla-leakage/sla-leakage-engine.SlaLeakageResult shape. Side-effect-free
 * (no React, no CSS) so render tests import the helpers directly.
 *
 * PLAIN-LANGUAGE LAW (package UI (b)): every class renders as a human title +
 * a one-line action hint readable by non-technical clinic staff -- NO
 * snake_case, NO internal jargon anywhere in the UI. The class KEYS below are
 * the wire contract only; the UI shows classMeta().title / .hint.
 *
 * SEE-NOT-DO: these types describe a READ-ONLY queue. V1 has no action; the
 * row + drawer are shaped so the V4-P1 assign/resolve controls drop in.
 */

import { formatIstDateTime } from '@/lib/datetime-ist';

// --- Wire shapes (mirror the backend SlaLeakageResult) ----------------------

export type SlaBreachClass =
  | 'unclaimed_lead_overdue'
  | 'qualified_no_appointment'
  | 'booked_not_confirmed'
  | 'stuck_in_stage'
  | 'no_activity';

export type SlaSeverity = 'neutral' | 'amber' | 'red';

export interface SlaTimelineEntryView {
  readonly label: string;
  readonly occurredAtIso: string | null;
  readonly actorRole: string | null;
}

export interface SlaBreachItemView {
  readonly breachClass: SlaBreachClass;
  readonly refKind: 'lead' | 'appointment';
  readonly safeLeadId: string | null;
  readonly appointmentId: number | null;
  readonly displayName: string;
  readonly organizationId: number;
  readonly clinicId: number | null;
  readonly clinicLabel: string | null;
  readonly assignedLabel: string | null;
  readonly stageLabel: string | null;
  readonly stuckSinceIso: string | null;
  readonly ageMinutes: number;
  readonly thresholdMinutes: number;
  readonly severity: SlaSeverity;
  /** Rider (c) STITCH-2: booked_not_confirmed whose scheduled date already
   *  passed -> the UI says "follow up to rebook" instead of "confirm". */
  readonly appointmentDatePassed?: boolean;
  readonly timeline: readonly SlaTimelineEntryView[];
}

export interface SlaClassSummaryView {
  readonly breachClass: SlaBreachClass;
  readonly count: number;
  readonly severity: SlaSeverity;
}

export interface SlaLeakageResult {
  readonly generatedAtMs: number;
  readonly thresholds: Readonly<Record<SlaBreachClass, number>>;
  readonly summary: readonly SlaClassSummaryView[];
  readonly items: readonly SlaBreachItemView[];
  readonly capped: boolean;
}

// --- Class presentation (plain-language title + one-line action hint) --------

export interface SlaClassMeta {
  /** Human, non-technical tile/section title. */
  readonly title: string;
  /** One-line action hint for non-technical staff. */
  readonly hint: string;
}

/** The five classes, in render order (matches the backend SLA_BREACH_CLASSES). */
export const SLA_CLASS_ORDER: readonly SlaBreachClass[] = [
  'unclaimed_lead_overdue',
  'qualified_no_appointment',
  'booked_not_confirmed',
  'stuck_in_stage',
  'no_activity',
];

const CLASS_META: Record<SlaBreachClass, SlaClassMeta> = {
  unclaimed_lead_overdue: {
    title: 'New leads waiting too long',
    hint: 'Assign someone to call them',
  },
  qualified_no_appointment: {
    title: 'Ready to book, no appointment',
    hint: 'Book their appointment now',
  },
  booked_not_confirmed: {
    title: 'Appointments not confirmed',
    hint: 'Confirm the appointment with the patient',
  },
  stuck_in_stage: {
    title: 'Stuck at the same step',
    hint: 'Move them forward or follow up',
  },
  no_activity: {
    title: 'Gone quiet',
    hint: 'Reach out and follow up',
  },
};

export function classMeta(key: SlaBreachClass): SlaClassMeta {
  return CLASS_META[key];
}

/**
 * LEAD-INBOX-SLA-AGING-AND-QUEUE-HEALTH-V1 (Refs #315): the compact ROW-CHIP /
 * summary label per class -- the five-second token the operator scans on a
 * queue row ("Unclaimed" -> "Unclaimed 14m"). classMeta().title is the
 * full-sentence tile HEADING (deliberately too long for a row chip), so the
 * short chip copy lives HERE, in the SAME one-place-per-copy module. Sentence
 * case. An unknown token degrades through classShortLabel to a neutral 'SLA
 * breach' (never echoed raw -- the clinical-canary posture).
 */
const CLASS_SHORT_LABEL: Record<SlaBreachClass, string> = {
  unclaimed_lead_overdue: 'Unclaimed',
  qualified_no_appointment: 'No appointment',
  booked_not_confirmed: 'Unconfirmed',
  stuck_in_stage: 'Stuck',
  no_activity: 'No activity',
};

const KNOWN_BREACH_CLASSES: ReadonlySet<string> = new Set<string>(SLA_CLASS_ORDER);

/** Type guard: is `v` one of the five known engine breach classes? */
export function isKnownBreachClass(v: string): v is SlaBreachClass {
  return KNOWN_BREACH_CLASSES.has(v);
}

/** The compact chip label for a breach class; unknown -> neutral 'SLA breach'. */
export function classShortLabel(key: string): string {
  return isKnownBreachClass(key) ? CLASS_SHORT_LABEL[key] : 'SLA breach';
}

/**
 * SLA-ONE-DERIVATION-AND-STATION-STRIP-V1 (Refs #315): per-class breach counts
 * over a set of rendered rows, each row given as its list of breach-class
 * tokens (classifier-verbatim chips/breaches). THE ONE frontend aggregation for
 * a client-derived SlaBreachStrip -- semantics match the backend
 * single-computation counts (lib/queue-health.ts computeIntakeQueueHealth.
 * slaBreachCounts and lib/coc-station/station-domain.deriveStationSlaBreachCounts)
 * exactly: a row is counted ONCE per KNOWN class it breaches (two same-class
 * breaches on one row count once), every class key is present (0 when none),
 * and an unknown/foreign token is IGNORED -- never counted, never echoed
 * (the clinical-canary posture; LES-056). Pure aggregation of the ONE V4-P0
 * classifier's output -- never a second evaluator, never a threshold read.
 */
export function slaBreachCountsForRows(
  rowsBreachClasses: readonly (readonly string[])[],
): Readonly<Record<SlaBreachClass, number>> {
  const counts: Record<SlaBreachClass, number> = {
    unclaimed_lead_overdue: 0,
    qualified_no_appointment: 0,
    booked_not_confirmed: 0,
    stuck_in_stage: 0,
    no_activity: 0,
  };
  for (const rowClasses of rowsBreachClasses) {
    const seen = new Set<SlaBreachClass>();
    for (const token of rowClasses) {
      if (isKnownBreachClass(token)) seen.add(token);
    }
    for (const cls of seen) counts[cls] += 1;
  }
  return counts;
}

/** Rider (c): the past-date rebook hint (copy lives in ONE place). */
export const APPOINTMENT_DATE_PASSED_HINT =
  'Appointment date has passed - follow up to rebook';

/** The per-item action hint: the class hint, or the rider-(c) rebook copy. */
export function itemActionHint(item: SlaBreachItemView): string {
  if (item.breachClass === 'booked_not_confirmed' && item.appointmentDatePassed === true) {
    return APPOINTMENT_DATE_PASSED_HINT;
  }
  return classMeta(item.breachClass).hint;
}

/**
 * Rider (b) STITCH-2: one-line SCOPE NOTE per class, derived from the ACTUAL
 * engine predicate (lib/sla-leakage/sla-leakage-engine.ts classifyLeakage) --
 * never a guess -- with the org's real threshold substituted. Rendered as the
 * tile tooltip so a manager never asks "which number is real". The unclaimed
 * note also names how it differs from Queue health's Untouched tile (every
 * unowned open lead, NO age filter -- lib/queue-health.ts untouchedCount).
 */
export function classScopeNote(
  key: SlaBreachClass,
  thresholdMinutes: number | null,
): string {
  const window =
    thresholdMinutes !== null ? formatLeakageAge(thresholdMinutes) : 'the SLA window';
  switch (key) {
    case 'unclaimed_lead_overdue':
      return `Counts leads with no owner for longer than ${window}. Queue health's Untouched counts every unowned lead regardless of age.`;
    case 'qualified_no_appointment':
      return `Counts leads qualified more than ${window} ago that still have no appointment.`;
    case 'booked_not_confirmed':
      return `Counts appointments still unconfirmed ${window} after booking.`;
    case 'stuck_in_stage':
      return `Counts leads that have not moved to a new step for ${window}.`;
    case 'no_activity':
      return `Counts leads with no recorded activity for ${window}.`;
  }
}

// --- Pure formatters --------------------------------------------------------

/**
 * Compact, two-granularity age label: "45m", "2h 15m", "3d". Minutes below an
 * hour show minutes; hours show hours (+ minutes when non-zero); a day or more
 * shows whole days. Null / negative -> "-".
 */
export function formatLeakageAge(minutes: number | null): string {
  if (minutes === null || !Number.isFinite(minutes) || minutes < 0) return '-';
  const m = Math.floor(minutes);
  if (m < 60) return `${m}m`;
  if (m < 1440) {
    const h = Math.floor(m / 60);
    const rem = m % 60;
    return rem === 0 ? `${h}h` : `${h}h ${rem}m`;
  }
  const d = Math.floor(m / 1440);
  return `${d}d`;
}

/**
 * Compact 12-hour IST timestamp for the ops timeline, e.g. "5 Jul 2026, 2:30 PM"
 * (LEAD-APPOINTMENT-CRM-COHERENCE-V1 SCOPE 6 - the 12-hour sweep; the old 24-hour
 * "2026-07-05 14:30" raw-slice mixed clock formats across surfaces). Deterministic
 * IST epoch arithmetic (no host-locale drift). Null / unparseable -> "-".
 */
export function formatTimestamp(iso: string | null): string {
  if (iso === null) return '-';
  return formatIstDateTime(iso) || '-';
}

/** The owner/assignee display: the label, or the sentinel for unassigned. */
export function assigneeLabel(item: SlaBreachItemView): string {
  return item.assignedLabel !== null && item.assignedLabel.trim().length > 0
    ? item.assignedLabel
    : 'Unassigned';
}

/** True when the item has no owner (renders the highlighted "Unassigned" badge). */
export function isUnassigned(item: SlaBreachItemView): boolean {
  return item.assignedLabel === null || item.assignedLabel.trim().length === 0;
}

/** The display name, or an honest placeholder when the carve is empty. */
export function displayNameOf(item: SlaBreachItemView): string {
  return item.displayName.trim().length > 0 ? item.displayName : 'Unnamed lead';
}

/** Items for one class (already oldest-first from the backend order). */
export function itemsForClass(
  view: SlaLeakageResult,
  key: SlaBreachClass,
): SlaBreachItemView[] {
  return view.items.filter((i) => i.breachClass === key);
}

/** The class summary entry, or a synthesized zero-neutral entry if absent. */
export function summaryFor(
  view: SlaLeakageResult,
  key: SlaBreachClass,
): SlaClassSummaryView {
  return (
    view.summary.find((s) => s.breachClass === key) ?? {
      breachClass: key,
      count: 0,
      severity: 'neutral',
    }
  );
}

/** Total breach count across all classes (may double-count across classes -- an
 *  item can breach more than one; this is the sum of class counts). */
export function totalBreachCount(view: SlaLeakageResult): number {
  return view.summary.reduce((acc, s) => acc + s.count, 0);
}
